import React, { useMemo, useState } from 'react'
import { useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { translate } from '@vatom/utils'
import { Button, theme } from '@vatom/wombo'
import * as Location from 'expo-location'
import { LocationAccuracy } from 'expo-location'
import { Actionsheet, Box, VStack } from 'native-base'

import { useModalQuery } from '../../../../components/PromptModal/PromptModal'
import { Title } from '../../../../components/Title'
import { useMaxWith } from '../../../../components/WebWrapper'
import ActionSheetHeader, { HeaderType } from '../../partials/ActionSheetHeader'
import { LatLng } from '../react-native-maps'

import { MapView } from './MapView'

type CurrentLocation = {
  latitude: number
  longitude: number
  latitudeDelta: number
  longitudeDelta: number
}

const getCurrentLocation = async () => {
  const currentPermission = await Location.getForegroundPermissionsAsync()
  if (currentPermission.granted === false && currentPermission.status === 'denied') {
    throw new Error('Location permission required')
  }
  if (currentPermission.status !== 'granted') {
    const requestPermission = await Location.requestForegroundPermissionsAsync()
    if (requestPermission.status !== 'granted') {
      throw new Error('Location permission required')
    }
  }

  const location = await Location.getCurrentPositionAsync({
    accuracy: LocationAccuracy.Balanced,
    distanceInterval: 5
  })

  return {
    latitude: location.coords.latitude,
    longitude: location.coords.longitude,
    latitudeDelta: 0.015,
    longitudeDelta: 0.0121
  }
}

interface IMap {
  onDrop: (lat: number, lon: number) => void
  onClose: () => void
  isLoading: boolean
  isOpen: boolean
}

export const Map = ({ onDrop, onClose, isLoading, isOpen }: IMap) => {
  const { height: screenHeight, width } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const [widthOfMap, setWidthOfMap] = useState(width)
  const maxWidth = useMaxWith()
  const [markerLocation, setMarkerLocation] = useState<LatLng>()

  const {
    isLoadingOrFetching,
    isError: isErrorPermission,
    renderModalQueryComponent,
    data: location
  } = useModalQuery({
    modalQueryKey: ['drop-location'],
    modalQueryFn: getCurrentLocation,
    onSuccessFn: location => {
      const l = location as CurrentLocation
      if (l) {
        setMarkerLocation(() => ({
          latitude: l.latitude,
          longitude: l.longitude
        }))
      }
    },
    promptError: {
      title: 'Location Permission',
      message: translate('errors.deniedLocation'),
      onCloseError: () => console.log('Prompt Error was closed')
    }
  })

  const renderMap = useMemo(
    () =>
      isOpen &&
      location && (
        <MapView
          setMarkerLocation={setMarkerLocation}
          widthOfMap={widthOfMap}
          setWidthOfMap={setWidthOfMap}
          location={location as CurrentLocation}
          markerLocation={markerLocation}
        />
      ),
    [isOpen, location, markerLocation, widthOfMap]
  )

  return (
    <Actionsheet.Content
      bg="white"
      pt="20px"
      pb="16px"
      maxHeight={screenHeight - insets.top}
      height={screenHeight - insets.top}
      maxWidth={maxWidth}
      margin={'auto'}
      _light={{
        backgroundColor: theme.colors.white
      }}
      _dark={{
        backgroundColor: theme.colors.grayDarkMode[900]
      }}
    >
      <VStack w="100%" flex={1}>
        <ActionSheetHeader
          type={HeaderType.close}
          onPress={onClose}
          title={translate('common.drop')}
          hasText={false}
          hasLearnMore={false}
        />
        <Box flex={1}>
          {renderModalQueryComponent}
          {renderMap}
        </Box>
        <Box paddingY={4}>
          <Button
            onPress={() => onDrop(markerLocation!.latitude, markerLocation!.longitude)}
            disabled={isLoading || isLoadingOrFetching || isErrorPermission}
            opacity={isLoading || isLoadingOrFetching || isErrorPermission ? 0.5 : 1}
            _light={{
              backgroundColor: theme.colors.systemColorsLight.orange
            }}
            _dark={{
              backgroundColor: theme.colors.systemColorsDark.orange
            }}
          >
            <Title preset="h5" variant="semibold" paddingY={1} color="white">
              {translate('common.drop')}
            </Title>
          </Button>
        </Box>
      </VStack>
    </Actionsheet.Content>
  )
}
export default Map
